import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, delay, retryWhen, scan } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class CustomHttpService {
    // Number of retry attempts after the first failure. Default: 3 retries.
    // This means initial attempt + up to `maxRetries` retry attempts.
    private readonly maxRetries = 3;
    // Delay in ms between retries (fixed). You can make this configurable if needed.
    private readonly retryDelayMs = 1000;

    constructor(private http: HttpClient) { }

    private withRetry<U>(obs: Observable<U>): Observable<U> {
        // Make this generic over the observable payload so callers can pass
        // HttpEvent<T> observables as well. We keep the signature general
        // and return the same observable type.
        return obs.pipe(
            // retryWhen gives control over retry logic. We use scan to count attempts.
            retryWhen(errors => errors.pipe(
                scan((retryCount, err) => {
                    if (retryCount >= this.maxRetries) {
                        // rethrow the error to break the retry loop
                        throw err;
                    }
                    return retryCount + 1;
                }, 0),
                // small delay between retries
                delay(this.retryDelayMs)
            )),
            catchError(err => {
                // let callers handle the error; preserve stack/message
                return throwError(() => err);
            })
        );
    }

    post<T = any>(url: string, body: any | null, options?: {
        headers?: HttpHeaders | {
            [header: string]: string | string[];
        };
        params?: HttpParams | {
            [param: string]: string | string[];
        };
        observe?: any;
        reportProgress?: boolean;
        responseType?: any;
        withCredentials?: boolean;
    }): Observable<T> {
        return this.withRetry<T>(this.http.post<T>(url, body, options) as Observable<T>);
    }

    get<T = any>(url: string, options?: any): Observable<T> {
        return this.withRetry<T>(this.http.get<T>(url, options) as Observable<T>);
    }

    put<T = any>(url: string, body: any | null, options?: any): Observable<T> {
        return this.withRetry<T>(this.http.put<T>(url, body, options) as Observable<T>);
    }

    delete<T = any>(url: string, options?: any): Observable<T> {
        return this.withRetry<T>(this.http.delete<T>(url, options) as Observable<T>);
    }

    patch<T = any>(url: string, body: any | null, options?: any): Observable<T> {
        return this.withRetry<T>(this.http.patch<T>(url, body, options) as Observable<T>);
    }
}
